/**
 * Wildlife scoring — §7.1.
 *
 * Each WildlifeRule kind is implemented as a pure function over the player's
 * board. The exhaustive switch on rule.kind ensures new kinds added to the
 * discriminated union are caught at compile time.
 *
 * Pure — no I/O, no mutations.
 */

import type { WildlifeRule } from "../types/card";
import type { AxialCoord } from "../types/coords";
import { coordKey, parseCoordKey } from "../types/coords";
import type { Board, PlayerState } from "../types/player";
import type { Wildlife } from "../types/wildlife";
import { axialDistance } from "../utils/axial";

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Returns all coords in the board that carry the given wildlife species. */
const wildlifeCoords = (
  board: Board,
  species: Wildlife,
): readonly AxialCoord[] =>
  Object.entries(board.cells)
    .filter(([, cell]) => cell.wildlife === species)
    .map(([key]) => parseCoordKey(key));

/**
 * Returns the axial coords of all cells adjacent to `coord` that are occupied
 * (have any tile) in `board`.
 */
const adjacentOccupiedCoords = (
  board: Board,
  coord: AxialCoord,
): readonly AxialCoord[] => {
  // Six neighbour offsets (dq, dr)
  const OFFSETS: readonly [number, number][] = [
    [1, -1],
    [1, 0],
    [0, 1],
    [-1, 1],
    [-1, 0],
    [0, -1],
  ];
  return OFFSETS.map(([dq, dr]) => ({
    q: coord.q + dq,
    r: coord.r + dr,
  })).filter((n) => board.cells[coordKey(n)] !== undefined);
};

/**
 * Groups a set of coords into connected components using simple adjacency
 * (any edge-adjacent cell with the same species).
 */
const groupByAdjacency = (
  coords: readonly AxialCoord[],
): readonly (readonly AxialCoord[])[] => {
  const keySet = new Set(coords.map(coordKey));
  const unvisited = new Set(keySet);
  const groups: Array<readonly AxialCoord[]> = [];
  const coordMap = new Map(coords.map((c) => [coordKey(c), c]));

  const OFFSETS: readonly [number, number][] = [
    [1, -1],
    [1, 0],
    [0, 1],
    [-1, 1],
    [-1, 0],
    [0, -1],
  ];

  for (const c of coords) {
    const startKey = coordKey(c);
    if (!unvisited.has(startKey)) {
      continue;
    }

    const group: AxialCoord[] = [];
    const queue: AxialCoord[] = [c];
    unvisited.delete(startKey);

    while (queue.length > 0) {
      const current = queue.shift()!;
      group.push(current);

      for (const [dq, dr] of OFFSETS) {
        const nKey = coordKey({ q: current.q + dq, r: current.r + dr });
        if (unvisited.has(nKey)) {
          unvisited.delete(nKey);
          queue.push(coordMap.get(nKey)!);
        }
      }
    }

    groups.push(group);
  }

  return groups;
};

// ---------------------------------------------------------------------------
// Straight-line detection across 3 hex axes
//
// Each token is assigned to the LONGEST maximal run it belongs to across all
// three axial directions. This ensures a lone token scores once (length=1),
// not three times. Two adjacent tokens form one length-2 run along their shared
// axis and length-1 runs along the other two axes; the length-2 run wins.
//
// Algorithm:
// 1. For each of the 3 axes, find all maximal runs and record each token's
//    run length in that axis.
// 2. For each token, keep the maximum run length across all axes.
// 3. Group tokens by their assigned (maximal) run to deduplicate; emit one
//    length entry per distinct maximal run.
// ---------------------------------------------------------------------------

const LINE_DIRECTIONS: readonly [number, number][] = [
  [1, 0], // q-axis
  [0, 1], // r-axis
  [1, -1], // s-axis
];

interface RunInfo {
  /** Canonical key identifying this maximal run (start coord + direction). */
  readonly runId: string;
  readonly length: number;
}

/**
 * For each token coord, returns the RunInfo for the longest maximal run that
 * token participates in across all 3 axes. Ties are broken by axis order.
 *
 * Then de-duplicates by runId so each run is emitted exactly once.
 */
const findLineLengths = (
  board: Board,
  species: Wildlife,
): readonly number[] => {
  const coords = wildlifeCoords(board, species);
  if (coords.length === 0) {
    return [];
  }

  const keySet = new Set(coords.map(coordKey));

  // For each token, compute its best (longest) run info across all axes
  const bestRunForToken = new Map<string, RunInfo>();

  for (const [dq, dr] of LINE_DIRECTIONS) {
    const visitedInAxis = new Set<string>();

    for (const start of coords) {
      const startKey = coordKey(start);
      if (visitedInAxis.has(startKey)) {
        continue;
      }

      // Walk backward to find the canonical start of this run
      let runStart = start;
      while (keySet.has(coordKey({ q: runStart.q - dq, r: runStart.r - dr }))) {
        runStart = { q: runStart.q - dq, r: runStart.r - dr };
      }

      // Walk forward to find all tokens in this run
      const runTokens: AxialCoord[] = [];
      let walker = runStart;
      while (keySet.has(coordKey(walker))) {
        visitedInAxis.add(coordKey(walker));
        runTokens.push(walker);
        walker = { q: walker.q + dq, r: walker.r + dr };
      }

      const runId = `${coordKey(runStart)}|${dq},${dr}`;
      const length = runTokens.length;

      for (const token of runTokens) {
        const tokenKey = coordKey(token);
        const existing = bestRunForToken.get(tokenKey);
        if (existing === undefined || length > existing.length) {
          bestRunForToken.set(tokenKey, { runId, length });
        }
      }
    }
  }

  // Each token is assigned to exactly one run. De-duplicate runs and emit lengths.
  const seenRunIds = new Set<string>();
  const lineLengths: number[] = [];

  for (const runInfo of bestRunForToken.values()) {
    if (!seenRunIds.has(runInfo.runId)) {
      seenRunIds.add(runInfo.runId);
      lineLengths.push(runInfo.length);
    }
  }

  return lineLengths;
};

// ---------------------------------------------------------------------------
// Public scoring functions (each matches a WildlifeRule kind)
// ---------------------------------------------------------------------------

/**
 * pointsPerGroupOfSize — §7.1.1, §7.1.2 etc.
 *
 * Finds all connected groups of `species`. Scores based on group size:
 * - "exact": only groups of exactly `size` score.
 * - "atLeast": groups of `size` or more score once each.
 * Optional `cap` limits total points.
 */
export const scoreGroupsOfSize = (
  board: Board,
  species: Wildlife,
  rule: Extract<WildlifeRule, { kind: "pointsPerGroupOfSize" }>,
): number => {
  const coords = wildlifeCoords(board, species);
  if (coords.length === 0) {
    return 0;
  }

  const groups = groupByAdjacency(coords);
  let total = 0;

  for (const group of groups) {
    if (rule.mode === "exact" && group.length === rule.size) {
      total += rule.points;
    } else if (rule.mode === "atLeast" && group.length >= rule.size) {
      total += rule.points;
    }
  }

  if (rule.cap !== undefined) {
    total = Math.min(total, rule.cap);
  }

  return total;
};

/**
 * pointsPerLine — §7.1.2 (hawk_C), §7.1 (elk_A, salmon_A etc.)
 *
 * Finds all maximal straight-line runs. Each run of length L scores
 * `pointsPerStraight[L]` if present; 0 otherwise. Only runs with
 * length >= minLength qualify.
 */
export const scoreLines = (
  board: Board,
  species: Wildlife,
  rule: Extract<WildlifeRule, { kind: "pointsPerLine" }>,
): number => {
  const lineLengths = findLineLengths(board, species);
  let total = 0;

  for (const length of lineLengths) {
    if (length < rule.minLength) {
      continue;
    }
    const pts = rule.pointsPerStraight[length] ?? 0;
    total += pts;
  }

  return total;
};

/**
 * pointsPerAdjacentDistinctSpeciesCount — §7.1 (fox_A, fox_F KS promo).
 *
 * For each tile carrying `species`, count the number of distinct OTHER species
 * in immediately adjacent (edge-adjacent) tiles. Look up that count in
 * `rule.counts`; if no entry, score 0 for that tile.
 */
export const scoreAdjacentDistinctSpecies = (
  board: Board,
  species: Wildlife,
  rule: Extract<
    WildlifeRule,
    { kind: "pointsPerAdjacentDistinctSpeciesCount" }
  >,
): number => {
  const coords = wildlifeCoords(board, species);
  let total = 0;

  for (const coord of coords) {
    const adjCoords = adjacentOccupiedCoords(board, coord);
    const distinctSpecies = new Set<string>();

    for (const adj of adjCoords) {
      const cell = board.cells[coordKey(adj)];
      if (
        cell?.wildlife !== null &&
        cell?.wildlife !== undefined &&
        cell.wildlife !== species
      ) {
        distinctSpecies.add(cell.wildlife);
      }
    }

    const count = distinctSpecies.size;
    total += rule.counts[count] ?? 0;
  }

  return total;
};

/**
 * pointsPerSurroundingTileCount — §7.1.2 (hawk_A).
 *
 * For each tile carrying `species`, count how many of its 6 hex neighbours
 * are occupied by ANY tile (regardless of wildlife). Look up that count in
 * `rule.counts`.
 */
export const scoreSurroundingTileCount = (
  board: Board,
  species: Wildlife,
  rule: Extract<WildlifeRule, { kind: "pointsPerSurroundingTileCount" }>,
): number => {
  const coords = wildlifeCoords(board, species);
  let total = 0;

  for (const coord of coords) {
    const surroundingCount = adjacentOccupiedCoords(board, coord).length;
    total += rule.counts[surroundingCount] ?? 0;
  }

  return total;
};

/**
 * pointsPerPairWithinDistance — §7.1.1 (bear_D).
 *
 * Counts all unique pairs of `species` tiles where axial distance ≤ `distance`.
 * Score = pairs × pointsPerPair, optionally capped.
 */
export const scorePairsWithinDistance = (
  board: Board,
  species: Wildlife,
  rule: Extract<WildlifeRule, { kind: "pointsPerPairWithinDistance" }>,
): number => {
  const coords = wildlifeCoords(board, species);
  let pairs = 0;

  for (let i = 0; i < coords.length; i++) {
    for (let j = i + 1; j < coords.length; j++) {
      if (axialDistance(coords[i]!, coords[j]!) <= rule.distance) {
        pairs++;
      }
    }
  }

  const raw = pairs * rule.pointsPerPair;
  return rule.cap !== undefined ? Math.min(raw, rule.cap) : raw;
};

// ---------------------------------------------------------------------------
// KS custom promo registry
// ---------------------------------------------------------------------------

/**
 * Registry of named KS promo scoring functions.
 * Add entries here as new promos are implemented.
 * Unregistered promoIds return 0 (safe fallback per spec §7.1.4).
 */
const KS_PROMO_REGISTRY: Readonly<
  Record<string, (board: Board, species: Wildlife) => number>
> = {};

// ---------------------------------------------------------------------------
// scoreWildlifeForPlayer — exhaustive dispatcher
// ---------------------------------------------------------------------------

/**
 * Scores the given wildlife rule for a player's board.
 * Exhaustive switch ensures all WildlifeRule kinds are handled.
 */
export const scoreWildlifeForPlayer = (
  player: PlayerState,
  rule: WildlifeRule,
  species: Wildlife,
): number => {
  const { board } = player;

  switch (rule.kind) {
    case "pointsPerGroupOfSize":
      return scoreGroupsOfSize(board, species, rule);

    case "pointsPerLine":
      return scoreLines(board, species, rule);

    case "pointsPerAdjacentDistinctSpeciesCount":
      return scoreAdjacentDistinctSpecies(board, species, rule);

    case "pointsPerSurroundingTileCount":
      return scoreSurroundingTileCount(board, species, rule);

    case "pointsPerPairWithinDistance":
      return scorePairsWithinDistance(board, species, rule);

    case "ksCustom": {
      const fn = KS_PROMO_REGISTRY[rule.promoId];
      return fn !== undefined ? fn(board, species) : 0;
    }

    default: {
      const _exhaustive: never = rule;
      return _exhaustive;
    }
  }
};
